import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout, AuthStatus } from '../components/AuthLayout';
import { InlineError } from '../components/Feedback';
import { TextField } from '../components/Form';
import { useAuth } from '../lib/auth';
import { signup } from '../lib/endpoints';
import { errorMessage } from '../lib/api';
import { useToast } from '../lib/toast';

type Phase = 'working' | 'failed' | 'no-token' | 'done';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Offers another verification email when a link has expired or been used already. */
function ResendPanel(): JSX.Element {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const send = async () => {
    setPending(true);
    setError(null);
    try {
      const response = await signup.resend(email.trim());
      setSent(true);
      toast.success('Email sent', response?.message ?? 'Check your inbox, and your spam folder.');
    } catch (cause) {
      const message = errorMessage(cause);
      setError(message);
      toast.error('Could not send the email', message);
    } finally {
      setPending(false);
    }
  };

  if (sent) {
    return (
      <p className="auth-neutral-note" role="status">
        If that address is waiting on confirmation, a new link is on its way. Check your spam folder
        too.
      </p>
    );
  }

  return (
    <div className="auth-resend">
      <TextField
        label="Send a new link to"
        type="email"
        value={email}
        onChange={setEmail}
        autoComplete="email"
        placeholder="you@company.com"
      />
      <InlineError message={error} />
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={() => void send()}
        disabled={pending || !EMAIL_PATTERN.test(email.trim())}
      >
        {pending ? 'Sending…' : 'Email me a new link'}
      </button>
    </div>
  );
}

export function ActivatePage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { adoptSession } = useAuth();

  const token = params.get('token') ?? '';
  const [phase, setPhase] = useState<Phase>(token === '' ? 'no-token' : 'working');
  const [message, setMessage] = useState<string | null>(null);
  // A token is single use, so the POST must not be repeated by StrictMode's double effect run.
  const started = useRef(false);

  useEffect(() => {
    if (token === '' || started.current) return;
    started.current = true;

    const controller = new AbortController();
    let active = true;

    signup
      .activate(token, controller.signal)
      .then((response) => {
        if (!active) return;
        if (response?.success && response.accessToken && response.user) {
          adoptSession(response.accessToken, response.user);
          setPhase('done');
          toast.success('Account activated', `Welcome, ${response.user.displayName}.`);
          navigate('/', { replace: true });
          return;
        }
        // Server said no (expired, already used, unknown token) or withheld a session.
        setPhase('failed');
        setMessage(
          response?.message ??
            'That confirmation link is no longer valid. Links expire, and each one works only once.',
        );
      })
      .catch((cause: unknown) => {
        if (!active) return;
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setPhase('failed');
        setMessage(errorMessage(cause));
      });

    return () => {
      active = false;
      // Deliberately not aborting: the activation must be allowed to finish server-side.
    };
  }, [token, adoptSession, navigate, toast]);

  if (phase === 'no-token') {
    return (
      <AuthLayout
        title="Confirm your account"
        subtitle="This link is missing its confirmation token."
        footer={
          <>
            <Link to="/signup">Create an account</Link>
            <Link to="/signin">Sign in</Link>
          </>
        }
      >
        <AuthStatus tone="warn" title="Nothing to confirm">
          <p>
            Open the link straight from the email — some mail clients trim the address. If that keeps
            failing, request a new link below.
          </p>
        </AuthStatus>
        <ResendPanel />
      </AuthLayout>
    );
  }

  if (phase === 'working' || phase === 'done') {
    return (
      <AuthLayout title="Confirming your account" subtitle="This only takes a moment.">
        <div className="auth-status" role="status" aria-live="polite">
          {/* Decorative: the wrapper is the live region announcing the text below. */}
          <span className="spinner-ring" aria-hidden="true" />
          <p className="auth-status-title">
            {phase === 'done' ? 'Signing you in…' : 'Checking your confirmation link…'}
          </p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="That link did not work"
      subtitle="Confirmation links expire and can only be used once."
      footer={
        <>
          <Link to="/signup">Create an account</Link>
          <Link to="/signin">Sign in</Link>
        </>
      }
    >
      <AuthStatus tone="bad" title="We could not confirm your account">
        <p>{message}</p>
        <p className="auth-status-hint">
          If you have already confirmed this address, just sign in. Otherwise send yourself a fresh
          link.
        </p>
      </AuthStatus>
      <ResendPanel />
    </AuthLayout>
  );
}
